import { Card, CardContent, CardHeader, CardTitle } from '@ais-ui/ui-core';
import { PiFlag, PiGavel, PiHandPalm } from 'react-icons/pi';
import { useMemo } from 'react';
import { useGetShipByImo } from '@ais-ui/api';
import { usePermissions } from '../../Permissions/PermissionsProvider';

interface StatusCardInterface {
  title: string;
  icon: React.ReactNode;
  value: string | number;
  description?: string;
  severity: 'low' | 'medium' | 'high';
}

export const StatusCard = ({
  title,
  icon,
  value,
  description,
  severity,
}: StatusCardInterface) => {
  return (
    <Card
      className={
        severity === 'high'
          ? 'bg-red-400'
          : severity === 'medium'
          ? 'bg-yellow-400'
          : ''
      }
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
};

interface IntegrityStatusCardsProps {
  imo: number;
}

export const IntegrityStatusCards = ({ imo }: IntegrityStatusCardsProps) => {
  const permissions = usePermissions();

  const hasShipDbPermissions =
    permissions.mdmAdvancedVesselInfo ||
    permissions.mdmRegister ||
    permissions.mdmIntegrity;

  const {
    data: extendedVesselInfo,
    isLoading: isLoadingExtendedVesselInfo,
    isError: isErrorExtendedVesselInfo,
  } = useGetShipByImo(imo, hasShipDbPermissions);

  const {
    flagStatusTokyoValue,
    tokyoSeverity,
  }: {
    flagStatusTokyoValue: string;
    tokyoSeverity: 'low' | 'medium' | 'high';
  } = useMemo(() => {
    if (!extendedVesselInfo?.advancedIntegrity?.flagPerformanceTokyo) {
      return { flagStatusTokyoValue: 'Unknown', tokyoSeverity: 'low' as const };
    }

    let severity: 'low' | 'medium' | 'high' = 'low';

    if (
      extendedVesselInfo.advancedIntegrity.flagPerformanceTokyo.toLowerCase() ===
      'black'
    ) {
      severity = 'high';
    } else if (
      extendedVesselInfo.advancedIntegrity.flagPerformanceTokyo.toLowerCase() ===
      'grey'
    ) {
      severity = 'medium';
    } else {
      severity = 'low';
    }

    return {
      flagStatusTokyoValue:
        extendedVesselInfo.advancedIntegrity.flagPerformanceTokyo,
      tokyoSeverity: severity,
    };
  }, [extendedVesselInfo]);

  const {
    flagStatusParisValue,
    parisSeverity,
  }: {
    flagStatusParisValue: string;
    parisSeverity: 'low' | 'medium' | 'high';
  } = useMemo(() => {
    if (!extendedVesselInfo?.advancedIntegrity?.flagPerformanceParis) {
      return { flagStatusParisValue: 'Unknown', parisSeverity: 'low' as const };
    }

    let severity: 'low' | 'medium' | 'high' = 'low';

    if (
      extendedVesselInfo.advancedIntegrity.flagPerformanceParis.toLowerCase() ===
      'black'
    ) {
      severity = 'high';
    } else if (
      extendedVesselInfo.advancedIntegrity.flagPerformanceParis.toLowerCase() ===
      'grey'
    ) {
      severity = 'medium';
    } else {
      severity = 'low';
    }

    return {
      flagStatusParisValue:
        extendedVesselInfo.advancedIntegrity.flagPerformanceParis,
      parisSeverity: severity,
    };
  }, [extendedVesselInfo]);

  const { isSanctioned, sanctionDescription } = useMemo(() => {
    if (!extendedVesselInfo?.advancedIntegrity?.sanctions) {
      return { isSanctioned: false, sanctionDescription: '' };
    }

    if (extendedVesselInfo.advancedIntegrity.sanctions.length > 0) {
      return {
        isSanctioned: true,
        sanctionDescription:
          extendedVesselInfo.advancedIntegrity.sanctions.length === 1
            ? 'Has 1 sanction'
            : `Has ${extendedVesselInfo.advancedIntegrity.sanctions.length} sanctions`,
      };
    }

    return { isSanctioned: false, sanctionDescription: '' };
  }, [extendedVesselInfo]);

  const { isBanned, banDescription } = useMemo(() => {
    if (!extendedVesselInfo?.advancedIntegrity?.bans) {
      return { isBanned: false, banDescription: '' };
    }

    if (extendedVesselInfo.advancedIntegrity.bans.length > 0) {
      return {
        isBanned: true,
        banDescription:
          extendedVesselInfo.advancedIntegrity.bans.length === 1
            ? 'Has 1 ban'
            : `Has ${extendedVesselInfo.advancedIntegrity.bans.length} bans`,
      };
    }

    return { isBanned: false, banDescription: '' };
  }, [extendedVesselInfo]);

  if (!permissions.mdmIntegrity) {
    return null;
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <StatusCard
        title={'Flag Status Tokyo'}
        icon={<PiFlag />}
        value={flagStatusTokyoValue}
        severity={tokyoSeverity}
      />
      <StatusCard
        title={'Flag Status Paris'}
        icon={<PiFlag />}
        value={flagStatusParisValue}
        severity={parisSeverity}
      />
      <StatusCard
        title="Sanction"
        icon={<PiGavel></PiGavel>}
        value={isSanctioned ? 'Sanctioned' : 'Not sanctioned'}
        description={sanctionDescription}
        severity={isSanctioned ? 'high' : 'low'}
      />
      <StatusCard
        title={'Ban'}
        value={isBanned ? 'Banned' : 'Not banned'}
        icon={<PiHandPalm />}
        description={banDescription}
        severity={isBanned ? 'high' : 'low'}
      />
    </div>
  );
};
